import { i18n } from "./i18n";
import { SUPPORTED_LANGUAGES ,SUPPORTED_LANGUAGES_LABEL, DEFAULT_LANGUAGE } from "@/shared/scripts/helpers/constants/trans";
import { LocaleMessageObject } from "vue-i18n";
import { Route, RawLocation } from "vue-router";
import { setCookie } from "@/shared/scripts/helpers/cookie/cookie";
import { replace } from "lodash";


/**
 * Translation file based on the follow recommandation from the lokalise website
 * https://lokalise.com/blog/vue-i18n/
 */

const Trans = {
    get defaultLocale (): string {
        return DEFAULT_LANGUAGE;
    },
    get supportedLocales(): Array<string> {
        return SUPPORTED_LANGUAGES_LABEL;
    },
    get supportedLocalesArray(): Array<Record<string, unknown>> {
        return SUPPORTED_LANGUAGES;
    },
    get currentLocale(): string {
        return i18n.locale;
    },
    set currentLocale(locale: string) {
        setCookie("LANGUAGE", locale,
            {
                "path": "/",
                "domain": process.env.VUE_APP_ROOT_URL,
                "httponly": false,
                "expires": "525600",
                "secure": process.env.VUE_APP_ENVIRONMENT_NAME !== "local"
            });
        i18n.locale = locale;
    },

    changeLocale: async function(locale: string): Promise<void|string> {
        if (!Trans.isLocaleSupported(locale)) {
            return Promise.reject(new Error("Locale not supported : " + locale));
        }
        if (i18n.locale === locale) {
            return Promise.resolve(locale);
        }
        return await Trans.loadLocaleFile(locale).then((msgs: LocaleMessageObject) => {
            i18n.setLocaleMessage(locale, (msgs.default as LocaleMessageObject) || msgs);
            return Trans.setI18nLocaleInServices(locale);
        });
    },

    isLocaleSupported: function(locale: string): boolean {
        return Trans.supportedLocales.includes(locale);
    },

    loadLocaleFile: async function(locale: string): Promise<LocaleMessageObject> {
        const lang = import(`@/shared/lang/${locale}.json`);
        const routes = import (`@/shared/lang/routes-${locale}.json`);
        return lang.then((langFile) => {
            return routes.then((routesFile) => {
                return Object.assign(langFile.default,routesFile.default);
            });
        });
    },

    setI18nLocaleInServices: function(locale: string): string {
        Trans.currentLocale = locale;
      document.querySelector("html")?.setAttribute("lang", locale);
      return locale;
    },

    routeMiddleware: async function(to: Route, from: Route, next: (to?: RawLocation | false | void) => void): Promise<string|void> {
        const locale = to.params.locale;
        if (!Trans.isLocaleSupported(locale)) {
            return next(Trans.defaultLocale);
        }
        return await Trans.changeLocale(locale).then(() => next());
    },

    i18nRoute: function(to: Route): Record<string, unknown> {
        return {
            ...to,
            params: {locale: this.currentLocale, ...to.params}
        };
    },

    pluralization: function(key: string, data: number): string {
        const pluralizeKey = i18n.t(key) as unknown as Record<string,string>;
        if (data == 0 || (data == 1 && pluralizeKey.hasOwnProperty("one"))) {
            return replace(pluralizeKey.one, "%d", data.toString());
        }

        if (pluralizeKey.hasOwnProperty("other")) {
            return replace(pluralizeKey.other, "%d", data.toString());
        }

        if(typeof(pluralizeKey) === "string") {
            return replace(pluralizeKey, "%d", data.toString());
        }

        return key;
    }
};
export { Trans };
